import { useEffect, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useSubmitPriceLine } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import type { PriceLineResponse } from '../../model/types';

interface TierRow {
  minQuantity: number;
  amount: string;
}

export interface PriceCellEditorDrawerProps {
  open: boolean;
  product: { id: string; name?: string };
  channelCode?: string;
  existingLines?: PriceLineResponse[];
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toIso(local: string): string | undefined {
  if (!local.trim()) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toLocal(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function seedTiers(
  existingLines: PriceLineResponse[] | undefined,
  channelCode: string | undefined,
): TierRow[] {
  const match = existingLines?.find((l) =>
    channelCode ? l.channelCode === channelCode : !l.channelCode,
  );
  if (match && match.tiers.length > 0) {
    return match.tiers.map((t) => ({ minQuantity: t.minQuantity, amount: String(t.amount) }));
  }
  return [{ minQuantity: 1, amount: '' }];
}

/** Right-side drawer to submit / schedule a governed price line for a (product, channel). */
export function PriceCellEditorDrawer({
  open,
  product,
  channelCode,
  existingLines,
  onClose,
  onResult,
}: PriceCellEditorDrawerProps) {
  const submit = useSubmitPriceLine();
  const pending = submit.isPending;

  const [tiers, setTiers] = useState<TierRow[]>([{ minQuantity: 1, amount: '' }]);
  const [compareAt, setCompareAt] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');

  useEffect(() => {
    if (!open) return;
    const effectiveChannel = channelCode || undefined;
    setTiers(seedTiers(existingLines, effectiveChannel));
    const existing = existingLines?.find((l) =>
      effectiveChannel ? l.channelCode === effectiveChannel : !l.channelCode,
    );
    setCompareAt(existing?.compareAtAmount != null ? String(existing.compareAtAmount) : '');
    setValidFrom(toLocal(existing?.validFrom));
    setValidTo(toLocal(existing?.validTo));
  }, [open, existingLines, channelCode, product.id]);

  const unitAmountStr = tiers[0]?.amount ?? '';
  const unitAmount = parseFloat(unitAmountStr);
  const wholesaleValid = tiers.slice(1).every((t) => {
    const amt = parseFloat(t.amount);
    return t.minQuantity >= 2 && !Number.isNaN(amt) && amt > 0;
  });
  const isValid = !Number.isNaN(unitAmount) && unitAmount > 0 && wholesaleValid;

  const setTierAmount = (index: number, value: string) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, amount: value } : t)));
  };

  const setTierQty = (index: number, value: string) => {
    const qty = parseInt(value, 10);
    if (!Number.isNaN(qty) && qty > 0) {
      setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, minQuantity: qty } : t)));
    }
  };

  const addTier = () => {
    setTiers((prev) => {
      const maxQty = prev.reduce((max, t) => Math.max(max, t.minQuantity), 0);
      const nextQty = maxQty > 0 ? maxQty + 1 : 10;
      return [...prev, { minQuantity: nextQty, amount: '' }];
    });
  };

  const removeTier = (index: number) => {
    if (index === 0) return;
    setTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const parsedTiers = tiers
      .map((t, i) => ({ minQuantity: i === 0 ? 1 : t.minQuantity, amount: parseFloat(t.amount) }))
      .filter((t) => t.minQuantity >= 1 && !Number.isNaN(t.amount) && t.amount > 0);

    const ca = parseFloat(compareAt);

    try {
      await submit.mutateAsync({
        productId: product.id,
        channelCode: channelCode || undefined,
        compareAtAmount: !Number.isNaN(ca) && ca > 0 ? ca : undefined,
        validFrom: toIso(validFrom),
        validTo: toIso(validTo),
        tiers: parsedTiers,
      });
      onResult('success', 'Цена сохранена');
      onClose();
    } catch (err: unknown) {
      onResult('error', (err as NormalizedApiError).message ?? 'Не удалось сохранить цену');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => onClose()}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 420 },
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {product.name ?? product.id}
        </Typography>
        {channelCode ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {channelCode}
          </Typography>
        ) : null}
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Unit price + compare-at */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Цена, ₽"
              required
              type="number"
              value={tiers[0]?.amount ?? ''}
              onChange={(e) => setTierAmount(0, e.target.value)}
              error={unitAmountStr !== '' && !isValid}
              slotProps={{ htmlInput: { 'data-testid': 'unit-amount-input', min: 0, step: '0.01' } }}
              fullWidth
            />
            <TextField
              label="Старая цена, ₽"
              type="number"
              value={compareAt}
              onChange={(e) => setCompareAt(e.target.value)}
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
              fullWidth
            />
          </Stack>

          {/* Validity window */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Действует с"
              type="datetime-local"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Действует по"
              type="datetime-local"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          {/* Governance hint */}
          <Alert severity="warning" icon="⚠" sx={{ fontSize: 13 }}>
            При сохранении цена проходит{' '}
            <strong>проверку (validate-on-write)</strong>: резкое падение уйдёт на{' '}
            <strong>утверждение</strong>, а не опубликуется сразу.
          </Alert>

          <Divider />

          {/* Wholesale tier editor */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Оптовые тиры
          </Typography>

          {tiers.slice(1).map((tier, i) => {
            const idx = i + 1;
            const rowAmt = parseFloat(tier.amount);
            const qtyInvalid = tier.minQuantity < 2;
            const amtInvalid = tier.amount !== '' && (Number.isNaN(rowAmt) || rowAmt <= 0);
            return (
              <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  label="от шт"
                  type="number"
                  value={tier.minQuantity || ''}
                  onChange={(e) => setTierQty(idx, e.target.value)}
                  error={qtyInvalid}
                  helperText={qtyInvalid ? 'мин. 2 шт' : ' '}
                  slotProps={{ htmlInput: { min: 2, step: 1 } }}
                  sx={{ width: 110 }}
                />
                <TextField
                  label="Цена, ₽"
                  type="number"
                  value={tier.amount}
                  onChange={(e) => setTierAmount(idx, e.target.value)}
                  error={amtInvalid}
                  helperText={amtInvalid ? '> 0 ₽' : ' '}
                  slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                  fullWidth
                />
                <IconButton
                  aria-label="удалить тир"
                  onClick={() => removeTier(idx)}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}

          <Button
            startIcon={<AddRoundedIcon />}
            onClick={addTier}
            variant="text"
            size="small"
            sx={{ alignSelf: 'flex-start', pl: 0 }}
          >
            Добавить тир
          </Button>
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={onClose} disabled={pending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!isValid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </Box>
    </Drawer>
  );
}
